/** REST client + little-endian binary parsers for the CAD backend. */

export interface ArrayMeta {
  name: string;
  components: number;
  range: [number, number];
}

export interface MeshResponse {
  mesh_id: string;
  upload_id: string;
  mode: string;
  density: number;
  target_size: number | null;
  watertight: boolean | null;
  n_tets: number | null;
  n_points: number;
  n_cells: number;
  quality: {
    scaled_jacobian?: {
      min: number;
      max: number;
      mean: number;
      p01: number;
      p05: number;
      p10: number;
      n_inverted: number;
      n_cells: number;
    };
  } | null;
  point_arrays: ArrayMeta[];
  cell_arrays: ArrayMeta[];
  default_color_by: string | null;
  geometry_url: string;
}

export interface UploadResponse {
  id: string;
  extension: string;
  n_faces: number;
  modes: string[];
  min_density: number;
  max_density: number;
  default_density: number;
}

export interface Geometry {
  vertices: Float32Array; // N*3
  faces: Uint32Array; // M*3
  normals: Float32Array | null; // N*3 or null
}

export interface ScalarArray {
  values: Float32Array; // count*components
  components: number;
  association: "points" | "cells";
  range: [number, number];
}

/** Parse the /geometry wire format into typed arrays. */
export function parseGeometry(buf: ArrayBuffer): Geometry {
  const dv = new DataView(buf);
  let off = 0;
  const nPts = dv.getUint32(off, true);
  off += 4;
  const vertices = new Float32Array(buf.slice(off, off + nPts * 12));
  off += nPts * 12;
  const nFaces = dv.getUint32(off, true);
  off += 4;
  const faces = new Uint32Array(buf.slice(off, off + nFaces * 12));
  off += nFaces * 12;
  const nNorm = dv.getUint32(off, true);
  off += 4;
  let normals: Float32Array | null = null;
  if (nNorm > 0) {
    normals = new Float32Array(buf.slice(off, off + nNorm * 12));
  }
  return { vertices, faces, normals };
}

const DTYPE_BYTES = [4, 8, 4, 4, 1]; // f32, f64, i32, u32, u8

/** Parse the /array wire format. Values are returned as Float32 (magnitude for vectors). */
export function parseArray(buf: ArrayBuffer): ScalarArray {
  const dv = new DataView(buf);
  const dtype = dv.getUint8(0);
  const components = dv.getUint8(1);
  const assoc = dv.getUint8(2);
  const count = dv.getUint32(4, true);
  const payloadOff = 8;
  const n = count * components;
  let raw: Float64Array | Float32Array | Int32Array | Uint32Array | Uint8Array;
  const slice = buf.slice(payloadOff, payloadOff + n * DTYPE_BYTES[dtype]);
  switch (dtype) {
    case 0:
      raw = new Float32Array(slice);
      break;
    case 1:
      raw = new Float64Array(slice);
      break;
    case 2:
      raw = new Int32Array(slice);
      break;
    case 3:
      raw = new Uint32Array(slice);
      break;
    default:
      raw = new Uint8Array(slice);
      break;
  }
  const rangeOff = payloadOff + n * DTYPE_BYTES[dtype];
  const range: [number, number] = [dv.getFloat32(rangeOff, true), dv.getFloat32(rangeOff + 4, true)];

  // Reduce to one scalar per element (magnitude for vectors).
  const values = new Float32Array(count);
  if (components === 1) {
    for (let i = 0; i < count; i++) values[i] = raw[i];
  } else {
    for (let i = 0; i < count; i++) {
      let s = 0;
      for (let c = 0; c < components; c++) {
        const v = raw[i * components + c];
        s += v * v;
      }
      values[i] = Math.sqrt(s);
    }
  }
  return { values, components, association: assoc === 1 ? "cells" : "points", range };
}

export class Api {
  constructor(private base = "") {}

  async uploadCad(file: File): Promise<UploadResponse> {
    const form = new FormData();
    form.append("file", file, file.name);
    const resp = await fetch(`${this.base}/api/cad/upload`, { method: "POST", body: form });
    if (!resp.ok) throw new Error(await detail(resp));
    return (await resp.json()) as UploadResponse;
  }

  async mesh(uploadId: string, mode: string, density: number): Promise<MeshResponse> {
    const url = `${this.base}/api/cad/${uploadId}/mesh?mode=${encodeURIComponent(mode)}&density=${density}`;
    const resp = await fetch(url, { method: "POST" });
    if (!resp.ok) throw new Error(await detail(resp));
    return (await resp.json()) as MeshResponse;
  }

  async geometry(meshId: string): Promise<Geometry> {
    const resp = await fetch(`${this.base}/api/cad/mesh/${meshId}/geometry`);
    if (!resp.ok) throw new Error(await detail(resp));
    return parseGeometry(await resp.arrayBuffer());
  }

  async array(meshId: string, name: string): Promise<ScalarArray> {
    const resp = await fetch(`${this.base}/api/cad/mesh/${meshId}/array/${encodeURIComponent(name)}`);
    if (!resp.ok) throw new Error(await detail(resp));
    return parseArray(await resp.arrayBuffer());
  }
}

async function detail(resp: Response): Promise<string> {
  try {
    const body = await resp.json();
    if (body && typeof body.detail === "string") return body.detail;
    if (body && body.detail != null) return String(body.detail);
  } catch {
    /* non-JSON */
  }
  return `HTTP ${resp.status}`;
}
